import AppButton from '../../../../../components/common/AppButton';
import AppDialog from '../../../../../components/common/AppDialog';
import React, { useEffect, useMemo } from 'react';
import moment from 'moment';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from '../../../../../context/SnackbarContextProvider';
import {
  useCustomTiming,
  CustomTimingFormValues,
} from '../../../../../context/CustomTimingContextProvider';
import { AnnouncementFormValues } from './types';
import AnnouncementType from './components/AnnouncementType';
import ReservationDetails from './components/ReservationDetails';
import Description from './components/Description';
import Recipients from './components/Recipients';
import Time from './components/Time';
import TriggerDueNotification from './components/TriggerDueNotification';
import Files from './components/Files';
import Equipment from './components/Equipment';
import Checklist from './components/Checklist';
import Task from './components/Task';
import { SVG } from '../../../../../constants/assetLists';
import QUERY_KEYS from '../../../../../constants/queryKeys';
import {
  handleCreateAnnouncementService,
  handleUpdateAnnouncementService,
} from '../../../../../services/operations/announcements';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { handleDepartmentManagersService } from '../../../../../services/users';
import { handleGetAllDepartmentsService } from '../../../../../services/operations/departments';
import { useOperationsContext } from '../../../../../context/OperationsContextProvider';
import { uploadFiles } from '../../../../../utils/uploadFiles';
import { getStartAndEndOfDay } from '../../../../../utils';
import {
  getLocationFilterParams,
  useClientsContext,
} from '../../../../../context/clients/ClientsContextProvider';
import { useLocation } from 'react-router';

type Props = {
  open: boolean;
  onClose: () => void;
  announcementData?: any;
  mode?: 'create' | 'edit' | 'add-task';
};

const embeddedTaskSchema = z.object({
  id: z.union([z.string(), z.number()]).optional(),
  taskTitle: z.string().min(1, 'כותרת המשימה היא שדה חובה').trim(),
  needApproval: z.boolean(),
  recipients: z.array(z.string()),
  files: z.array(z.any()),
  equipments: z.array(z.any()),
  checklists: z.array(z.string()),
});

const announcementSchema = z
  .object({
    announcementTitle: z.string(),
    announcementType: z
      .string()
      .refine(
        (value) =>
          [
            'Regular',
            'Error',
            'Training',
            'Administration',
            'Regulation',
            'Notice',
            'Quick',
            'Reservation',
            'Menu',
          ].includes(value),
        { message: 'יש לבחור סוג הודעה תקין' },
      ),
    reservation_zone: z.string(),
    number_of_people: z.string(),
    phone: z.string(),
    client_name: z.string(),
    description: z.string().optional(),
    // Other fields matching the exact types
    showInBrief: z.boolean(),
    showInCalendar: z.boolean(),
    isUrgent: z.boolean(),
    enabledDeletion: z.boolean(),
    branchIds: z.array(z.string()),
    subBranchIds: z.array(z.string()),
    departments: z.union([z.literal('all'), z.array(z.string())]).optional(),
    assignee: z.array(z.string()),
    enableRespondentCount: z.boolean(),
    triggerDueNotification: z.boolean(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    schedulingType: z.string(),
    startTime: z.string().optional(),
    endTime: z.string().optional(),
    time: z.string().optional(),
    dayOfWeek: z.number().optional(),
    forms: z.string().optional(),

    files: z.array(z.any()),
    equipments: z.array(z.any()),
    checklists: z.array(z.string()),
    tasks: z.array(embeddedTaskSchema),
  })
  .superRefine((data, ctx) => {
    // -------------------------
    // Existing business rules
    // -------------------------
    if (
      (data.announcementType === 'Training' ||
        data.announcementType === 'Regulation') &&
      typeof data.enableRespondentCount !== 'boolean'
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'עבור סוג הכשרה או תקנה יש להגדיר אפשרות מספר מגיבים',
        path: ['enableRespondentCount'],
      });
    }

    // if (data.announcementType === "Regulation") {
    //   if (!data.forms || !data.forms.trim()) {
    //     ctx.addIssue({
    //       code: "custom",
    //       message: "עבור סוג תקנה יש לבחור טופס",
    //       path: ["forms"],
    //     });
    //   }
    // }

    // -------------------------
    // ✅ New timing rules
    // -------------------------
    const requiresStartDate = [
      'default',
      'all_day',
      'by_day_and_time',
      'between_days_and_hours',
    ].includes(data.schedulingType);

    const requiresEndDate = ['default', 'between_days_and_hours'].includes(
      data.schedulingType,
    );

    const requiresStartEndTime = [
      'by_day_and_time',
      'between_days_and_hours',
      'daily_at_fixed_time',
      'custom',
    ].includes(data.schedulingType);

    if (requiresStartDate && (!data.startDate || !data.startDate.length)) {
      ctx.addIssue({
        code: 'custom',
        message:
          data.schedulingType === 'all_day'
            ? 'עבור תזמון לכל היום יש להגדיר תאריך'
            : 'יש להגדיר תאריך התחלה',
        path: ['startDate'],
      });
    }

    if (requiresEndDate && (!data.endDate || !data.endDate.length)) {
      ctx.addIssue({
        code: 'custom',
        message: 'יש להגדיר תאריך סיום',
        path: ['endDate'],
      });
    }

    if (requiresStartEndTime) {
      if (
        !data.startTime ||
        !data.startTime.length ||
        !moment(data.startTime, 'HH:mm', true).isValid()
      ) {
        ctx.addIssue({
          code: 'custom',
          message: 'יש להגדיר שעת התחלה',
          path: ['startTime'],
        });
      }
      if (
        !data.endTime ||
        !data.endTime.length ||
        !moment(data.endTime, 'HH:mm', true).isValid()
      ) {
        ctx.addIssue({
          code: 'custom',
          message: 'יש להגדיר שעת סיום',
          path: ['endTime'],
        });
      }
    }
    const annTitle = data.announcementTitle?.trim() || '';
    if (!annTitle && data.announcementType !== 'Reservation') {
      ctx.addIssue({
        code: 'custom',
        message: 'יש להגדיר כותרת להודעה',
        path: ['announcementTitle'],
      });
    }

    if (data.announcementType === 'Reservation') {
      const reservationZone = data.reservation_zone?.trim() || '';
      const numberOfPeople = data.number_of_people?.trim() || '';
      const phone = data.phone?.trim() || '';
      const clientName = data.client_name?.trim() || '';

      if (!reservationZone) {
        ctx.addIssue({
          code: 'custom',
          message: 'יש לבחור אזור הזמנה',
          path: ['reservation_zone'],
        });
      }

      if (!numberOfPeople) {
        ctx.addIssue({
          code: 'custom',
          message: 'יש להגדיר מספר סועדים',
          path: ['number_of_people'],
        });
      } else if (!/^\d+$/.test(numberOfPeople) || Number(numberOfPeople) <= 0) {
        ctx.addIssue({
          code: 'custom',
          message: 'יש להגדיר מספר סועדים תקין',
          path: ['number_of_people'],
        });
      }

      if (!phone) {
        ctx.addIssue({
          code: 'custom',
          message: 'יש להגדיר טלפון',
          path: ['phone'],
        });
      }

      if (!clientName) {
        ctx.addIssue({
          code: 'custom',
          message: 'יש להגדיר שם לקוח',
          path: ['client_name'],
        });
      }
    }

    if (data?.startTime && data?.endTime && data?.startTime === data?.endTime) {
      ctx.addIssue({
        code: 'custom',
        path: ['endTime'],
        message: 'שעת הסיום חייבת להיות אחרי שעת ההתחלה',
      });
    }
  });

function getUserDepartmentIds(user: any): string[] {
  const dept = user?.departmentId;
  if (dept === 'all') return ['all'];
  if (Array.isArray(dept)) return dept.map((d: any) => d._id);
  if (dept && typeof dept === 'object' && dept._id) return [dept._id];
  return [];
}

/**
 * List/detail API returns `departments` as `'all'`, `[]`, or populated refs
 * `[{ _id, name?, ... }]`. Legacy grouped responses used `originalDepartments`
 * (string ids). Form expects `'all' | string[]`.
 */
function normalizeAnnouncementDepartmentsForForm(
  announcement: any,
): 'all' | string[] | undefined {
  const raw = announcement?.departments;
  if (raw === 'all') return 'all';
  if (raw === undefined || raw === null) return undefined;
  if (Array.isArray(raw)) {
    if (raw.length === 0) return [];
    return raw
      .map((d: any) => (typeof d === 'string' ? d : d?._id))
      .filter(Boolean);
  }
  return undefined;
}

function AddAnnouncement({
  open,
  onClose,
  announcementData,
  mode = 'create',
}: Props) {
  return (
    <AddAnnouncementContent
      open={open}
      onClose={onClose}
      announcementData={announcementData}
      mode={mode}
    />
  );
}

function AddAnnouncementContent({
  open,
  onClose,
  announcementData,
  mode = 'create',
}: Props) {
  const [isUploadingFiles, setIsUploadingFiles] = React.useState(false);
  const [hasScrolledToTasks, setHasScrolledToTasks] = React.useState(false);
  const [expandedSections, setExpandedSections] = React.useState<string[]>(
    mode === 'add-task' ? ['tasks'] : [],
  );
  const location = useLocation();

  const {selectedClient, selectedLocation} = useClientsContext();
  const {setExpandAnnouncementIndex, selectedAnnouncementId, calendarSelectedDate} =
    useOperationsContext();
  const { showSnackbar } = useSnackbar();
  const { customTimingData, setCustomTimingData, clearCustomTimingData } =
    useCustomTiming();
  const queryClient = useQueryClient();
  const calendarStartTime = location?.state?.calendarStartTime as
    | string
    | undefined;
  const calendarEndTime = location?.state?.calendarEndTime as
    | string
    | undefined;

  const isAnnouncementCompleted = useMemo(() => {
    if (!announcementData?.isCompleted) return false;
    const tasks = announcementData?.tasks ?? [];
    return tasks.length === 0 || tasks.every((t: any) => t.isCompleted);
  }, [announcementData]);

  const createAnnouncementMutation = useMutation({
    mutationKey: [QUERY_KEYS.CREATE_ANNOUNCEMENT],
    mutationFn: handleCreateAnnouncementService,
    onSuccess: (data) => {
      // Invalidate and refetch announcements data
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_ANNOUNCEMENTS],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_ANNOUNCEMENTS_BY_DATE],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_ANNOUNCEMENTS_FOR_STATS],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_BRIEF_ANNOUNCEMENTS_BY_DATE],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_NOTIFICATIONS],
      });
      if (data?.data?._id) setExpandAnnouncementIndex(data.data._id);
      showSnackbar({
        message: data?.message ?? 'הודעה נוצרה בהצלחה',
        type: 'success',
      });
      handleModalClose();
    },
  });

  const updateAnnouncementMutation = useMutation({
    mutationKey: [QUERY_KEYS.UPDATE_ANNOUNCEMENT],
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: Record<string, any>;
    }) => handleUpdateAnnouncementService(id, payload),
    onSuccess: (data) => {
      // Invalidate and refetch announcements data
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_ANNOUNCEMENTS],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_ANNOUNCEMENTS_BY_DATE],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_ANNOUNCEMENTS_FOR_STATS],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_BRIEF_ANNOUNCEMENTS_BY_DATE],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_NOTIFICATIONS],
      });
      if (selectedAnnouncementId) {
        queryClient.invalidateQueries({
          queryKey: [
            QUERY_KEYS.GET_SINGLE_ANNOUNCEMENT,
            selectedAnnouncementId,
          ],
        });
      }

      if (data?.data?._id) setExpandAnnouncementIndex(data.data._id);

      showSnackbar({
        message: data?.message ?? 'הודעה עודכנה בהצלחה',
        type: 'success',
      });
      handleModalClose();
    },
  });
  const getDefaultValues = () => {
    const defaults = {
      announcementTitle: '',
      announcementType: 'Regular',
      reservation_zone: '',
      number_of_people: '',
      phone: '',
      client_name: '',
      showInBrief: false,
      showInCalendar: false,
      isUrgent: false,
      enabledDeletion: false,
      description: '',
      branchIds: [],
      subBranchIds: [],
      departments: undefined,
      assignee: [],
      enableRespondentCount: false,
      triggerDueNotification: true,
      startDate: moment().format('YYYY-MM-DD'),
      endDate: moment().format('YYYY-MM-DD'),
      schedulingType: 'default',
      startTime: '',
      endTime: '',
      time: '',
      dayOfWeek: 0,
      forms: '',
      files: [],
      equipments: [{ value: '' }],
      checklists: [],
      tasks: [],
    };

    if ((mode === 'edit' || mode === 'add-task') && announcementData) {
      return {
        ...defaults,
        announcementTitle: announcementData.announcementTitle || '',
        announcementType: announcementData.announcementType || 'Regular',
        reservation_zone:
          typeof announcementData.reservation_zone === 'string'
            ? announcementData.reservation_zone
            : announcementData.reservation_zone?._id || '',
        number_of_people:
          announcementData.number_of_people !== undefined &&
          announcementData.number_of_people !== null
            ? String(announcementData.number_of_people)
            : '',
        phone: announcementData.phone || '',
        client_name: announcementData.client_name || '',
        showInBrief: announcementData.showInBrief || false,
        showInCalendar: announcementData.showInCalendar || false,
        isUrgent: announcementData.isUrgent || false,
        enabledDeletion: announcementData.enabledDeletion || false,
        description: announcementData.description || '',
        branchIds: announcementData.branchIds || [],
        subBranchIds: announcementData.subBranchIds || [],
        departments: normalizeAnnouncementDepartmentsForForm(announcementData),
        assignee:
          announcementData.assignee?.map((item: { _id: string }) => item._id) ||
          [],
        enableRespondentCount: announcementData.enableRespondentCount || false,
        triggerDueNotification:
          announcementData.triggerDueNotification || true,
        startDate: announcementData.scheduling?.startDate
          ? moment
              .utc(announcementData.scheduling.startDate)
              .format('YYYY-MM-DD')
          : moment().format('YYYY-MM-DD'),
        endDate: announcementData.scheduling?.endDate
          ? moment.utc(announcementData.scheduling.endDate).format('YYYY-MM-DD')
          : moment().format('YYYY-MM-DD'),
        schedulingType:
          announcementData.scheduling?.schedulingType || 'default',
        startTime: announcementData.scheduling?.startTime || '',
        endTime: announcementData.scheduling?.endTime || '',
        time: announcementData.scheduling?.time || '',
        dayOfWeek: announcementData.scheduling?.dayOfWeek || 0,
        forms:
          typeof announcementData.forms === 'string'
            ? announcementData.forms
            : announcementData.forms?._id || '',
        files: announcementData.files || [],
        equipments:
          announcementData.equipments?.length > 0
            ? announcementData.equipments?.map((item: any) => {
                if (typeof item === 'string') {
                  return {
                    value: item,
                  };
                } else {
                  return {
                    value: item?._id,
                  };
                }
              })
            : [{ value: '' }],
        checklists:
          announcementData.checklists?.map(
            (item: { _id: string }) => item._id,
          ) || [],
        tasks:
          announcementData.tasks?.map((task: any) => ({
            ...task,
            id: task._id, // Map _id to id for form state
            comments:
              task.comments?.map((comment: { _id: string }) => comment._id) ||
              [],
            checklists:
              task.checklists?.map(
                (checklist: { _id: string }) => checklist._id,
              ) || [],
            recipients:
              task.recipients?.map((user: { _id: string }) => user._id) || [],
            equipments:
              task.equipments?.length > 0
                ? task.equipments?.map((item: any) => {
                    if (typeof item === 'string') {
                      return {
                        value: item,
                      };
                    } else {
                      return {
                        value: item?._id,
                      };
                    }
                  })
                : [{ value: '' }],
          })) || [],
      };
    }
    if (calendarStartTime && calendarEndTime) {
      return {
        ...defaults,
        showInCalendar: true,
        schedulingType: 'by_day_and_time',
        startDate: moment(calendarSelectedDate).format('YYYY-MM-DD'),
        endDate: '',
        startTime: calendarStartTime,
        endTime: calendarEndTime,
      };
    }


    const { branchId, subBranchId } = getLocationFilterParams(selectedLocation);
    return {
      ...defaults,
      branchIds: branchId ? [branchId] : [],
      subBranchIds: subBranchId ? [subBranchId] : [],
    };
  };

  const methods = useForm<AnnouncementFormValues>({
    resolver: zodResolver(announcementSchema),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: getDefaultValues(),
  });

  const { isDirty } = methods.formState;

  const selectedBranchId: string[] = methods.watch('branchIds');
  const selectedSubBranchId: string[] = methods.watch('subBranchIds');

  const announcementType = methods.watch('announcementType');
  const announcementTitle = methods.watch('announcementTitle');
  const isReservationAnnouncement = announcementType === 'Reservation';

  const { data: allManagers } = useQuery({
    queryKey: [
      QUERY_KEYS.GET_DEPARTMENT_MANAGERS,
      selectedClient?._id,
      selectedBranchId,
      selectedSubBranchId,
    ],
    queryFn: () =>
      handleDepartmentManagersService({
        clientId: selectedClient?._id,
        ...(selectedBranchId.length > 0 && { branchId: selectedBranchId }),
        ...(selectedSubBranchId.length > 0 && {
          subBranchId: selectedSubBranchId,
        }),
      }),
    enabled: !!selectedClient?._id,
  });

  const { data: allDepartments } = useQuery({
    queryKey: [
      QUERY_KEYS.GET_ALL_DEPARTMENTS,
      selectedClient?._id,
      selectedBranchId,
      selectedSubBranchId,
    ],
    queryFn: () =>
      handleGetAllDepartmentsService({
        clientId: selectedClient?._id,
        ...(selectedBranchId.length > 0 && { branchId: selectedBranchId }),
        ...(selectedSubBranchId.length > 0 && {
          subBranchId: selectedSubBranchId,
        }),
      }),
    enabled: !!selectedClient?._id,
  });

  // Reset form when modal opens with new data
  useEffect(() => {
    if (open) {
      const newValues = getDefaultValues();
      methods.reset(newValues);
      setHasScrolledToTasks(false);
      setExpandedSections(mode === 'add-task' ? ['tasks', 'task'] : []);
    }
  }, [
    open,
    mode,
    calendarStartTime,
    calendarEndTime,
    calendarSelectedDate,
  ]);

  useEffect(() => {
    if (!open) return;

    // when editing custom schedule, hydrate context
    if (
      (mode === 'edit' || mode === 'add-task') &&
      announcementData?.scheduling?.schedulingType === 'custom'
    ) {
      const customData = announcementData?.scheduling?.customConfig;

      const updatedData: CustomTimingFormValues = {
        repeatEvery: customData?.repeatEvery || 1,
        repeatType: customData?.repeatType || 'weekly',
        monthlyDates: customData?.monthlyDates || [],
        endCondition: customData?.endCondition || 'never',
        weeklyDays: customData?.weeklyDays || [],
        ...(customData?.endDate && { endDate: customData.endDate }),
        ...(customData?.occurrences && { occurrences: customData.occurrences }),
      };

      setCustomTimingData(updatedData);
    } else {
      // not custom => clear context
      clearCustomTimingData();
    }
  }, [open, mode, announcementData?._id]);

  const buildCustomConfig = () => {
    if (!customTimingData) return {};

    const config: any = {
      repeatType: customTimingData.repeatType,
      endCondition: customTimingData.endCondition,
    };

    // Add repeatEvery only if greater than 1
    if (customTimingData.repeatEvery) {
      config.repeatEvery = customTimingData.repeatEvery;
    }

    // Add repeat-specific fields
    if (
      customTimingData.repeatType === 'weekly' &&
      customTimingData.weeklyDays?.length > 0
    ) {
      config.weeklyDays = customTimingData.weeklyDays;
    }

    if (
      customTimingData.repeatType === 'monthly' &&
      customTimingData.monthlyDates?.length > 0
    ) {
      config.monthlyDates = customTimingData.monthlyDates;
    }

    // Add end condition fields
    if (
      customTimingData.endCondition === 'end_date' &&
      customTimingData.endDate
    ) {
      config.endDate = moment(customTimingData.endDate)
        .endOf('day')
        .toISOString();
    }

    if (
      customTimingData.endCondition === 'occurrences' &&
      customTimingData.occurrences
    ) {
      config.occurrences = customTimingData.occurrences;
    }

    return config;
  };

  const buildSchedulingPayload = (data: AnnouncementFormValues) => {
    // Return null if no scheduling type is selected or empty string
    if (!data.schedulingType || data.schedulingType === '') {
      return null;
    }

    const { startOfDay } = getStartAndEndOfDay(data.startDate as string);
    const { endOfDay } = getStartAndEndOfDay(data.endDate as string);

    const basePayload = {
      schedulingType: data.schedulingType,
      ...(data.schedulingType === 'custom' ||
      data.schedulingType === 'daily_at_fixed_time'
        ? {}
        : { startDate: startOfDay }),
    };

    switch (data.schedulingType) {
      case 'default':
        return {
          ...basePayload,
          endDate: endOfDay,
        };

      case 'by_day_and_time':
        return {
          ...basePayload,
          startTime: data.startTime,
          endTime: data.endTime,
        };

      case 'between_days_and_hours':
        return {
          ...basePayload,
          endDate: endOfDay,
          startTime: data.startTime,
          endTime: data.endTime,
        };

      case 'daily_at_fixed_time':
        return {
          ...basePayload,
          startTime: data.startTime,
          endTime: data.endTime,
        };

      case 'custom':
        return {
          ...basePayload,
          startTime: data.startTime,
          endTime: data.endTime,
          customConfig: buildCustomConfig(),
        };

      default:
        return basePayload;
    }
  };

  const getDepartManager = (
    department: AnnouncementFormValues['departments'],
  ) => {
    const allDeptIds =
      allDepartments?.departments?.map((dept: any) => dept?._id) || [];

    let targetDeptIds: string[] | null = null;
    if (department === 'all') {
      targetDeptIds = allDeptIds;
    } else if (
      Array.isArray(department) &&
      department.length === 1 &&
      department[0] === 'all'
    ) {
      targetDeptIds = allDeptIds;
    } else if (Array.isArray(department) && department.length > 0) {
      targetDeptIds = department;
    }

    if (!targetDeptIds || targetDeptIds.length === 0) return null;

    const results =
      allManagers?.data
        ?.filter((manager: any) => {
          const mgrDeptId = manager?.departmentId;
          // Manager belongs to all departments
          if (mgrDeptId === 'all') return true;
          // Manager has an array of departments — check intersection
          if (Array.isArray(mgrDeptId)) {
            const mgrDeptIds = mgrDeptId.map((d: any) =>
              typeof d === 'object' ? d?._id : d,
            );
            return mgrDeptIds.some((id: string) => targetDeptIds.includes(id));
          }
          return false;
        })
        ?.map((manager: any) => manager._id) || [];
    return results?.length > 0 ? results : null;
  };

  const initialType = location?.state?.initialType as string | undefined;

  useEffect(() => {
    if (location?.state?.showInCalendar) {
      methods.setValue('showInCalendar', true);
    }
    if (initialType) {
      const stored = localStorage.getItem('user');
      const user = stored && stored !== 'undefined' ? JSON.parse(stored) : null;

      methods.setValue('announcementType', initialType, {
        shouldValidate: true,
        shouldDirty: true,
      });

      if (initialType === 'Reservation') {
        methods.setValue('showInCalendar', true);
        methods.setValue('schedulingType', 'by_day_and_time');

        const restaurantDept = allDepartments?.departments?.find(
          (d: any) => d.name === 'מסעדה',
        );
        if (restaurantDept?._id) {
          methods.setValue('departments', [restaurantDept._id]);
        }
      } else if (initialType === 'Quick') {
        methods.setValue('showInBrief', true);

        const userDepts = getUserDepartmentIds(user);
        if (userDepts.length > 0) methods.setValue('departments', userDepts);
      }

      methods.trigger([
        'announcementTitle',
        'reservation_zone',
        'number_of_people',
        'phone',
        'client_name',
      ]);
    }
  }, [location?.state, allDepartments]);

  const onSubmit = async (data: AnnouncementFormValues) => {
    try {
      setIsUploadingFiles(true);
      const scheduling = buildSchedulingPayload(data);

      // Remove scheduling-related fields from main payload since they're in scheduling object
      const {
        startDate,
        endDate,
        startTime,
        endTime,
        time,
        dayOfWeek,
        schedulingType,
        enableRespondentCount,
        files,
        tasks,
        departments,
        branchIds,
        subBranchIds,
        enabledDeletion,
        reservation_zone,
        number_of_people,
        phone,
        client_name,
        ...cleanData
      } = data;

      let uploadedFiles: Array<{ url: string; type: string }> = [];
      let processedTasks: any = tasks;

      // Handle files: separate existing URLs from new files to upload
      if (files && files.length > 0) {
        // Separate existing URLs from new files
        const existingFiles = files.filter(
          (file: any) =>
            typeof file === 'string' ||
            (file.url && typeof file.url === 'string'),
        );
        const newFiles = files.filter(
          (file: any) =>
            file instanceof File || (file && !file.url && file.name),
        );

        // Upload only new files
        let newUploadedFiles: Array<{ url: string; type: string }> = [];
        if (newFiles.length > 0) {
          newUploadedFiles = await uploadFiles(newFiles);
        }

        // Combine existing URLs with newly uploaded files
        const processedExistingFiles = existingFiles.map((file: any) => ({
          url: typeof file === 'string' ? file : file.url,
          type: typeof file === 'string' ? 'unknown' : file.type || 'unknown',
        }));

        uploadedFiles = [...processedExistingFiles, ...newUploadedFiles];
      }

      // Process tasks and upload their files if any
      if (tasks && tasks.length > 0) {
        processedTasks = await Promise.all(
          tasks
            ?.filter((task) => task.taskTitle)
            .map(async (task) => {
              const taskReceipent = getDepartManager(data.departments);

              if (task.files && task.files.length > 0) {
                // Separate existing URLs from new files for tasks too
                const existingTaskFiles = task.files.filter(
                  (file: any) =>
                    typeof file === 'string' ||
                    (file.url && typeof file.url === 'string'),
                );
                const newTaskFiles = task.files.filter(
                  (file: any) =>
                    file instanceof File || (file && !file.url && file.name),
                );

                // Upload only new task files
                let newTaskUploadedFiles: Array<{ url: string; type: string }> =
                  [];
                if (newTaskFiles.length > 0) {
                  newTaskUploadedFiles = await uploadFiles(newTaskFiles);
                }

                // Combine existing URLs with newly uploaded files for tasks
                const processedExistingTaskFiles = existingTaskFiles.map(
                  (file: any) => ({
                    url: typeof file === 'string' ? file : file.url,
                    type:
                      typeof file === 'string'
                        ? 'unknown'
                        : file.type || 'unknown',
                  }),
                );

                const taskUploadedFiles = [
                  ...processedExistingTaskFiles,
                  ...newTaskUploadedFiles,
                ];

                return {
                  ...task,
                  recipients:
                    task.recipients?.length > 0
                      ? task.recipients
                      : taskReceipent
                        ? taskReceipent
                        : data.assignee,
                  equipments: task.equipments
                    ?.filter((ele) => ele.value)
                    ?.map((ele) => ele.value),
                  files: taskUploadedFiles,
                };
              }

              return {
                ...task,
                recipients:
                  task.recipients?.length > 0
                    ? task.recipients
                    : taskReceipent
                      ? taskReceipent
                      : data.assignee,
                equipments: task.equipments
                  ?.filter((ele) => ele.value)
                  ?.map((ele) => ele.value),
              };
            }),
        );
      }

      const payload: any = {
        ...cleanData,
        clientId: announcementData?.clientId || selectedClient?._id,
        branchIds: announcementData?._id ? branchIds : selectedBranchId,
        subBranchIds: announcementData?._id
          ? subBranchIds
          : selectedSubBranchId,
        enabledDeletion,
        ...(departments &&
          (Array.isArray(departments) || departments === 'all') && {
            departments: departments?.[0] === 'all' ? 'all' : departments,
          }),
        equipments: data.equipments
          ?.filter((ele) => ele.value)
          ?.map((ele) => ele.value),
        files: uploadedFiles,
        tasks: processedTasks,
        ...(scheduling && { scheduling }),
        ...(data.announcementType === 'Reservation' && {
          reservation_zone,
          number_of_people: Number(number_of_people),
          phone,
          client_name,
        }),
      };

      // Conditionally add fields based on announcementType
      // now show for all types
      // if (
      //   data.announcementType === 'Training' ||
      //   data.announcementType === 'Regulation'
      // ) {
      // }
      payload.enableRespondentCount = enableRespondentCount;

      // Use appropriate mutation based on mode
      if (announcementData?._id) {
        if (isAnnouncementCompleted && isDirty) {
          createAnnouncementMutation.mutate(payload);
        } else {
          updateAnnouncementMutation.mutate({
            id: announcementData._id,
            payload,
          });
        }
      } else {
        createAnnouncementMutation.mutate(payload);
      }
    } catch (error: any) {
      showSnackbar({
        message: error?.message || 'שגיאה בהעלאת הקבצים',
        type: 'error',
      });
    } finally {
      setIsUploadingFiles(false);
    }
  };

  const handleModalClose = () => {
    methods.reset();
    onClose();
  };

  const handleExpandSection = (section: string) => {
    setExpandedSections((prev) =>
      prev.includes(section)
        ? prev.filter((item) => item !== section)
        : [...prev, section],
    );
  };

  // Handle task section expansion and scrolling for add-task mode (only once)
  useEffect(() => {
    if (open && mode === 'add-task' && !hasScrolledToTasks) {
      setTimeout(() => {
        setExpandedSections((prev) =>
          prev.includes('task') ? prev : [...prev, 'task'],
        );

        setTimeout(() => {
          const tasksSection = document.querySelector('[data-section="tasks"]');
          if (tasksSection) {
            tasksSection.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
            setHasScrolledToTasks(true);
          }
        }, 400);
      }, 300);
    }
  }, [open, mode, hasScrolledToTasks]);

  return (
    <>
      <AppDialog
        showModel={open}
        onClose={handleModalClose}
        containerClasses="overflow-hidden"
        showHeaderLine={false}
        titleField={
          isReservationAnnouncement ? undefined : (
            <Controller
              name="announcementTitle"
              control={methods.control}
              render={({ field }) => {
                return (
                  <div className="w-full">
                    <div className="bg-grayBg px-1 py-2 rounded-tl-[4px] rounded-tr-[4px] border-b-[3px] border-b-linkBlue flex items-center gap-1">
                      {!field.value && (
                        <span className="m-0 text-[28px] font-medium text-raspberryRed">
                          *
                        </span>
                      )}

                      <input
                        className="w-full m-0 text-[28px] text-black bg-transparent outline-none placeholder:text-black"
                        placeholder={'כותרת'}
                        autoComplete="off"
                        value={field.value}
                        onChange={(e) =>
                          methods.setValue(
                            `announcementTitle`,
                            e.target.value,
                            {
                              shouldValidate: true,
                              shouldDirty: true,
                              shouldTouch: true,
                            },
                          )
                        }
                      />
                    </div>

                    {methods?.formState.errors.announcementTitle?.message && (
                      <p className="m-0 text-[12px] text-raspberryRed">
                        {methods?.formState.errors.announcementTitle?.message}
                      </p>
                    )}
                  </div>
                );
              }}
            />
          )
        }
        subTitle={
          !isReservationAnnouncement && !announcementTitle
            ? 'תופיע ברשימת ההודעות גם אם תוכן ההודעה סגור'
            : ''
        }
        width={717}
        fullHeight
        draggable
        titleIcon={
          <img
            src={SVG.menuDarkIcon}
            alt="menu"
            className="w-[18px] h-[12px] object-contain"
          />
        }
      >
        <div className="w-full h-full flex flex-col gap-4">
          <FormProvider {...methods}>
            <div className="flex-1 min-h-0 flex flex-col gap-3 overflow-y-auto px-[32px]">
              {/* Announcement Type */}
              <AnnouncementType allDepartments={allDepartments} />

              {isReservationAnnouncement && <ReservationDetails />}

              {/* Description */}
              <Description />

              {/* Recipients */}
              <Recipients
                forForm="announcement"
                expandedSections={expandedSections}
              />

              {/* Time — hidden for Quick type */}
              {announcementType !== 'Quick' && <Time />}

              {/* Trigger due notification — visible for all types */}
              <TriggerDueNotification />

              {/* Files */}
              <Files
                forForm="announcement"
                expandedSections={expandedSections}
              />

              {announcementType !== 'Quick' &&
                announcementType !== 'Reservation' && (
                  <div className="flex flex-col gap-6">
                    {/* Equipment */}
                    <Equipment
                      forForm="announcement"
                      handleExpandSection={handleExpandSection}
                      expandedSections={expandedSections}
                    />

                    {/* Checklist */}
                    <Checklist
                      forForm="announcement"
                      handleExpandSection={handleExpandSection}
                      expandedSections={expandedSections}
                    />

                    {/* Task */}
                    <div data-section="tasks">
                      <Task
                        handleExpandSection={handleExpandSection}
                        expandedSections={expandedSections}
                        setExpandedSections={setExpandedSections}
                      />
                    </div>
                  </div>
                )}
            </div>
          </FormProvider>

          {/* Footer */}
          <div className="flex justify-end items-center gap-2 px-[32px]">
            {/* <button className="text-xl font-bold text-linkBlue underline">
              אופציות נוספות
            </button> */}

            <div className="flex items-center gap-2">
              <AppButton onClick={handleModalClose}>ביטול</AppButton>

              <AppButton
                variant="primary"
                onClick={methods.handleSubmit(onSubmit)}
                disabled={
                  createAnnouncementMutation.isPending ||
                  updateAnnouncementMutation.isPending ||
                  isUploadingFiles ||
                  !methods.formState.isValid
                  // !isSelectedDepartOrAssignee
                }
              >
                {isUploadingFiles
                  ? 'מעלה קבצים...'
                  : createAnnouncementMutation.isPending ||
                      updateAnnouncementMutation.isPending
                    ? mode === 'edit'
                      ? 'מעדכן...'
                      : 'שומר...'
                    : mode === 'edit'
                      ? 'עדכון'
                      : 'שמירה'}
              </AppButton>
            </div>
          </div>
        </div>
      </AppDialog>
    </>
  );
}

export default AddAnnouncement;
